// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getItem } from '@/items/api/get-item';
import { ItemField } from '@/items/api/types';
import { updateItem } from '@/items/api/update-item';
import { CreateItemFieldDialog } from '@/items/components/create-item-field-dialog';
import { ItemFieldCard } from '@/items/components/item-field-card';
import { ItemFieldSection } from '@/items/components/item-field-section';
import {
  isItemFieldCardData,
  isItemFieldSectionData,
} from '@/items/components/types';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { Box, Button, Collapse, Fade, Stack, Typography } from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Navigate, createFileRoute } from '@tanstack/react-router';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Local state for drag-and-drop field ordering.
 * Outer array = sections, inner arrays = field IDs in display order.
 */
type ItemCustomFieldState = string[][];

function getFieldState(fields: ItemField[]): ItemCustomFieldState {
  const state: ItemCustomFieldState = [[], []];

  fields.forEach((field) => {
    if (field.section < state.length) {
      return state[field.section].push(field.id);
    }

    state[field.section] = [field.id];
  });

  return state;
}

export const Route = createFileRoute('/_app/catalog/items/$itemKey/')({
  component: ItemIndexRoute,
});

function ItemIndexRoute() {
  const { itemKey } = Route.useParams();

  const { data } = useSuspenseQuery({
    queryKey: ['item', itemKey],
    queryFn: () => getItem(itemKey),
  });

  // SERVICE items don't have fields - redirect to audit
  if (!data.item.isPhysical && !data.item.hasAssets) {
    return (
      <Navigate
        to="/catalog/items/$itemKey/audit"
        params={{ itemKey }}
        replace
      />
    );
  }

  // BULK items don't have fields - redirect to units
  if (data.item.isPhysical && !data.item.hasAssets) {
    return (
      <Navigate
        to="/catalog/items/$itemKey/units"
        params={{ itemKey }}
        replace
      />
    );
  }

  // DEVICE items show fields
  return <ItemFieldsContent itemKey={itemKey} />;
}

function ItemFieldsContent(props: { itemKey: string }) {
  const dialog = useDisclosure();
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery({
    queryKey: ['item', props.itemKey],
    queryFn: () => getItem(props.itemKey),
  });

  const mutation = useMutation({
    mutationFn: (
      fields: { fieldId: string; order: number; section: number }[]
    ) => {
      return updateItem(props.itemKey, { fields });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['item', props.itemKey] });
      setState(getFieldState(data.item.fields));
    },
  });

  const [state, setState] = useState(() => getFieldState(data.item.fields));

  useEffect(() => {
    setState((state) => {
      const count = state.reduce((count, section) => count + section.length, 0);

      if (data.item.fields.length !== count) {
        return getFieldState(data.item.fields);
      }

      return state;
    });
  }, [data.item.fields]);

  const reorderField = useCallback(
    (sectionIndex: number, sourceIndex: number, destinationIndex: number) => {
      setState((state) => {
        return state.map((section, index) => {
          if (index === sectionIndex) {
            return reorder({
              list: section,
              startIndex: sourceIndex,
              finishIndex: destinationIndex,
            });
          }

          return section;
        });
      });
    },
    []
  );

  const moveField = useCallback(
    (
      sourceSection: number,
      sourceField: number,
      destinationSection: number,
      destinationIndex: number
    ) => {
      setState((state) => {
        return state.map((section, sectionIndex) => {
          if (sectionIndex === sourceSection) {
            return section.filter((_, index) => index !== sourceField);
          }

          if (sectionIndex === destinationSection) {
            return [
              ...section.slice(0, destinationIndex),
              state[sourceSection][sourceField],
              ...section.slice(destinationIndex),
            ];
          }

          return section;
        });
      });
    },
    []
  );

  useEffect(() => {
    return monitorForElements({
      onDrop: ({ location }) => {
        if (location.current.dropTargets.length === 0) {
          return;
        }

        const [sourceField, sourceSection] = location.initial.dropTargets;

        if (!isItemFieldCardData(sourceField.data)) {
          return;
        }

        if (!isItemFieldSectionData(sourceSection.data)) {
          return;
        }

        if (location.current.dropTargets.length === 1) {
          const [destinationSection] = location.current.dropTargets;

          if (!isItemFieldSectionData(destinationSection.data)) {
            return;
          }

          const destinationFields = state[destinationSection.data.index];

          if (sourceSection.data.index === destinationSection.data.index) {
            const destinationIndex = getReorderDestinationIndex({
              startIndex: sourceField.data.index,
              indexOfTarget: destinationFields.length - 1,
              closestEdgeOfTarget: null,
              axis: 'vertical',
            });

            return reorderField(
              sourceSection.data.index,
              sourceField.data.index,
              destinationIndex
            );
          }

          return moveField(
            sourceSection.data.index,
            sourceField.data.index,
            destinationSection.data.index,
            destinationFields.length
          );
        }

        if (location.current.dropTargets.length === 2) {
          const [destinationField, destinationSection] =
            location.current.dropTargets;

          if (!isItemFieldCardData(destinationField.data)) {
            return;
          }

          if (!isItemFieldSectionData(destinationSection.data)) {
            return;
          }

          const closestEdgeOfTarget = extractClosestEdge(destinationField.data);

          if (sourceSection.data.index === destinationSection.data.index) {
            const destinationIndex = getReorderDestinationIndex({
              closestEdgeOfTarget,
              startIndex: sourceField.data.index,
              indexOfTarget: destinationField.data.index,
              axis: 'vertical',
            });

            return reorderField(
              sourceSection.data.index,
              sourceField.data.index,
              destinationIndex
            );
          }

          const destinationIndex =
            closestEdgeOfTarget === 'bottom'
              ? destinationField.data.index + 1
              : destinationField.data.index;

          moveField(
            sourceSection.data.index,
            sourceField.data.index,
            destinationSection.data.index,
            destinationIndex
          );
        }
      },
    });
  }, [state, moveField, reorderField]);

  const hasChanges = useMemo(() => {
    if (isEqual(state, getFieldState(data.item.fields))) {
      return false;
    }

    return true;
  }, [data, state]);

  const insertOrder = data.item.fields.reduce((order, field) => {
    return Math.max(order, field.order + 1);
  }, 0);

  const isEmpty = state.every((section) => section.length === 0);
  const rowCount = state.reduce((count, section) => {
    const sectionCount = section.reduce((count, fieldId) => {
      const field = data.item.fields.find(({ id }) => id === fieldId);

      if (field === undefined) {
        return count;
      }

      return field.type === 'multiline_text' ? count + 2 : count + 1;
    }, 0);

    return Math.max(count, sectionCount);
  }, 0);

  const handleClickReset = () => {
    setState(getFieldState(data.item.fields));
  };

  const handleClickSave = () => {
    const updates: { fieldId: string; order: number; section: number }[] = [];

    state.forEach((section, sectionIndex) => {
      section.forEach((id, fieldIndex) => {
        const field = data.item.fields.find((field) => field.id === id);

        if (field === undefined) {
          return;
        }

        if (field.order === fieldIndex && field.section === sectionIndex) {
          return;
        }

        updates.push({
          fieldId: field.id,
          order: fieldIndex,
          section: sectionIndex,
        });
      });
    });

    if (updates.length > 0) {
      mutation.mutate(updates);
    }
  };

  return (
    <HardfinTabCardWrapper
      title="Fields"
      actions={
        <Fade in={!hasChanges}>
          <Button
            variant="contained"
            color="secondary"
            onClick={dialog.open}
            startIcon={<AddRounded fontSize="small" />}
          >
            Add field
          </Button>
        </Fade>
      }
    >
      <Stack paddingTop={2}>
        <CreateItemFieldDialog
          section={0}
          order={insertOrder}
          itemKey={props.itemKey}
          isOpen={dialog.isOpen}
          onClose={dialog.close}
        />
        <Box
          gap={1}
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
          gridTemplateRows={{
            xs: `repeat(${state.length}, 1fr)`,
            sm: `repeat(${Math.ceil(state.length / 2)}, 1fr)`,
          }}
        >
          {isEmpty ? (
            <Typography
              color="text.secondary"
              textAlign="center"
              gridColumn={{ xs: 'span 1', sm: 'span 2' }}
            >
              This item has no fields
            </Typography>
          ) : (
            state.map((section, sectionIndex) => (
              <ItemFieldSection
                key={sectionIndex}
                count={rowCount}
                index={sectionIndex}
              >
                {section.map((fieldId, fieldIndex) => {
                  const field = data.item.fields.find(
                    ({ id }) => id === fieldId
                  );

                  if (field === undefined) {
                    return null;
                  }

                  return (
                    <ItemFieldCard
                      key={fieldId}
                      field={field}
                      index={fieldIndex}
                      itemKey={props.itemKey}
                      isEditable={!hasChanges}
                      isDisabled={mutation.isPending}
                    />
                  );
                })}
              </ItemFieldSection>
            ))
          )}
        </Box>
      </Stack>
      <Collapse in={hasChanges}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          paddingTop={2}
          paddingBottom={1}
        >
          <Button onClick={handleClickReset}>Reset</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleClickSave}
          >
            Save order
          </Button>
        </Stack>
      </Collapse>
    </HardfinTabCardWrapper>
  );
}
